import { Component } from '@angular/core';
import { OrderWrapperComponent } from "../../component/wrapper/order-wrapper.component";
import { Base } from '../../component/wrapper/base-wrapper.component';
import { Cart, GetIntentReturn } from '../../../../globalTypes/order.types';
import { Store } from '@ngrx/store';
import { ordersFeature } from '../../store/order.store';
import { lastValueFrom, take, takeUntil } from 'rxjs';
import { Logger } from '../../helper/logger';
import { accountFeature } from '../../store/account.store';
import { AddressPublic } from '../../../../globalTypes/account.types';
import { ButtonComponent } from "../../component/layout/element/button.component";
import { AddressData, AddressDialog } from '../account/address.dialog';
import {loadStripe, Stripe, StripeElements} from '@stripe/stripe-js';
import { MatDialog } from '@angular/material/dialog';
import { MatRadioModule } from '@angular/material/radio';
import { environment } from '../../../environments/environment';
import { httpsCallable } from 'firebase/functions';
import { Functions } from '@angular/fire/functions';
import { doStockCheck, updateCartWithTransactionId, UpdateCartWithTransactionIdVariables } from '@dataconnect/account';
import { FormsModule } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { authUserFeature } from '../../store/authUser.store';
import { OrderService } from '../../services/order.service';
import { CheckoutRateSelectorComponent } from "../../component/shipping/checkout-rate-selector.component";
import { LoadingLogoComponent } from "../../component/layout/element/loading-logo.component";
import { onCallFunctions } from '../../../../globalTypes/backend.api.types';
import { LimiterDirective } from '../../directive/limiter';
import { LOCATION_LABEL } from '../../../../globalTypes/configs';
const log = new Logger("CheckoutComponent")

@Component({
  selector: 'msh-checkout',
  imports: [OrderWrapperComponent, MatRadioModule, FormsModule, ButtonComponent, CheckoutRateSelectorComponent, LoadingLogoComponent, LimiterDirective],
  template: `
  <msh-order-wrapper
    [order]="order"
  >
  <div class=" max-w-[650px] m-auto">
    @if(!initComplete){
      <msh-loading-logo class=" w-96 h-96"></msh-loading-logo>
    }@else if(successMessage){
      <div class="text-center my-7 font-bold" >{{successMessage}}</div>
    } @else {
      <div class="flex justify-around">
        <div>
          <div class="font-bold capitalize">{{LOCATION_LABEL}} Shipping Address</div>
          <div class="mt-4">
            @if(address){
              <div>{{address.attn}} {{locationName}}</div>
              <div>{{address.streetNumber}} {{address.address}}</div>
              <div>{{address.city}}, {{address.state}} {{address.postcode}}</div>
            }
            <div (click)="editAddress()" class="lil-action pt-1">edit address</div>
          </div>
        </div>
        <!-- Once the ability to store billing details is implemented, this section will display them 
        <div>
          <div class="font-bold">Store Billing Details</div>
          <div class="mt-4">
            <div class="null">No details stored</div>
          </div>
        </div>
        -->
      </div>
      <!-- Shipping -->
      <div>
        @if(order){
        <!-- Select shipping rates from Shippo -->
          <msh-checkout-rate-selector
            [orderItems]="order.orderItems_on_order"
            [orderId]="order.id"
            [locationId]="locationId"
            (shippingSelectedChanged)="checkShippingMethod($event)"
          ></msh-checkout-rate-selector>
        }
      </div>
      <div class="flex flex-col justify-center items-center mt-10">
        <div class="font-bold">Additional Comments</div>
        <textarea [(ngModel)]="customerComments" class="" [limiter]="1000" rows="7" placeholder="I wanted to let you know..."></textarea>
      </div>
      <div>
        <div id="payment-element" style="w-[560px]"></div>
        <div id="payment-message" class="hidden text-center text-red-500 font-bold mb-10"></div>
        <msh-button
          [cta]="cta"
          [disabled]="stripeFormIsIncomplete || noShippingMethod"
          class="mt-4"
          (onClick)="progressCheckout()"
          [isLoading]="loadingStripeElements"
        ></msh-button>
        <div class="null my-6 text-center">Sellers will only receive payment after the order has been marked as fulfilled</div>

      </div>
    }
  </div>
  </msh-order-wrapper>
  `,
  styles: ``
})
export class CheckoutComponent extends Base<CheckoutComponent> {
  order!: Cart
  locationId = "";
  locationName = "";
  paymentIntentId = "";
  noShippingMethod = true;
  address!: AddressPublic
  loadingStripeElements = false;
  customerComments="";
  elements!:StripeElements
  paymentElement!: any
  cta = 'Calculate and proceed';
  mode = 'needs_cc_form';
  stripe: Stripe | null = null;
  returnUrl?: string;
  initComplete = false;
  stripeFormIsIncomplete = false;
  successMessage = '';
  LOCATION_LABEL = LOCATION_LABEL;

  // Stripe appearance configuration
  appearance = {
    theme: 'stripe',
    variables: {
      colorPrimary: '#E48133',
    },
  };

  constructor(
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private orderService: OrderService,
    private store: Store,
    private functions: Functions,
  ){
    super();
    // Subscribe to cart changes and update order details accordingly
    this.store.select(ordersFeature.selectCart).pipe(takeUntil(this.$destroy)).subscribe(async cart => {
      log.log({cart})
      if(!cart) return;
      this.order = JSON.parse(JSON.stringify(cart)) as Cart
      if(!this.order || !this.order.id) return;
      this.returnUrl = this.getRootURL() + "/order/purchase/" + this.order.id
      this.noShippingMethod = !this.order.orderItems_on_order.every(item => item.rateId || item.fixedShippingCost !== undefined)
      console.log({returnUrl: this.returnUrl})
    }) 
    // Subscribe to location changes and update address
    this.store.select(accountFeature.selectLocation).pipe(takeUntil(this.$destroy)).subscribe( async (location) => {
      if(!location) return;
      if(location?.shippingAddress) this.address = location?.shippingAddress
      this.locationId = location.id;
      this.locationName = location.name;
      this.stripe = await loadStripe(environment.stripe.key);
      this.initComplete = true;
    })
  }

  getRootURL() { // this is needed for the flexibility of using different subdomains
    const protocol = window.location.protocol;
    const hostname = window.location.hostname;
    const port = window.location.port;
    
    let rootURL = `${protocol}//${hostname}`;
    
    // Add port if it exists and is non-default
    if (port) {
      const defaultPorts: { [key: string]: string } = { 'http:': '80', 'https:': '443' };
      if (port !== defaultPorts[protocol]) {
        rootURL += `:${port}`;
      }
    }
    
    return rootURL;
  }

  checkShippingMethod(event: {rateId:string, shippingLotId: string}){ 
    console.log({shippingSelection: event})
    const { rateId } = event;
    this.noShippingMethod = rateId === '' || !this.order.orderItems_on_order.every(item => item.rateId || item.fixedShippingCost !== undefined)
  }

  async editAddress(){
    const data: AddressData = {
      address: this.address,
      addressType: 'shipping'
    }
    const dialog = this.dialog.open(AddressDialog, {data});
    const results = await lastValueFrom(dialog.afterClosed().pipe(take(1)))
    log.log({results})
  }

  async progressCheckout(){
    if(!this.stripe || !this.order || !this.order.id || !this.returnUrl) {
      this.snackBar.open("Something has gone wrong. Please try refreshing.");
      return;
    }
    const authUser = await lastValueFrom(this.store.select(authUserFeature.selectUser).pipe(take(1)));
    // TODO if no ACH details load CC element
    if(this.mode === 'needs_cc_form'){
      this.loadingStripeElements = true;
      // callable get intent
      let intent: GetIntentReturn | undefined
      try {
        const callable = httpsCallable<{return_url: string, orderId: string}, GetIntentReturn>(this.functions, onCallFunctions.getIntent);
        const getIntentReturn = await callable({return_url: this.returnUrl, orderId: this.order.id});
        log.log({getIntentReturn});
        intent = getIntentReturn.data as GetIntentReturn;
      } catch(error:any){
        console.log({error})
        this.loadingStripeElements = false;
        this.showMessage("Error getting payment intent: " + error.message || "Error getting payment intent" );
      }
      if(!intent || !intent.clientSecret) return;

      this.paymentIntentId = intent.paymentIntentId
      this.orderService.hydrateCart(this.order.id, this.locationId);
      this.elements = this.stripe.elements({appearance: {"theme":"flat"}, clientSecret: intent.clientSecret});

      this.paymentElement = this.elements.create("payment");
      this.paymentElement.mount("#payment-element");
      this.paymentElement.on('change', (event: any) => {
        this.stripeFormIsIncomplete = !event.complete
      })
      this.paymentElement.on('ready', () => {
        console.log("Stripe payment element ready")
        this.loadingStripeElements = false;
      })
      this.cta = 'Pay Now';
      this.mode = 'pay_with_cc_now';

    } else if (this.mode === 'pay_with_cc_now'){
      this.loadingStripeElements = true;
      this.stripeFormIsIncomplete = false;
      // save intent id to the order
      const updateOrderVars: UpdateCartWithTransactionIdVariables = { 
        locationId: this.locationId,
        orderId: this.order.id,
        paymentTransactionId: this.paymentIntentId,
      }
      if(this.customerComments){
        updateOrderVars.customerComments = this.customerComments;
      }
      for(let item of this.order.orderItems_on_order){
        if(item.isDigitalDownload) continue;
        const stockRef = await doStockCheck({listingVariationId: item.listingVariation.id})
        const stock = stockRef.data
        if((stock.stockCheck?.quantity || 0) < item.quantity){
          this.snackBar.open("Stock conflict: Stock levels changed as you were confirming your order and there is not enough to complete your purchase", "Close");
          this.loadingStripeElements = false;
          return;
        }
      }
      try {
        const orderMutation = await updateCartWithTransactionId(updateOrderVars); // this updates the order statusId = "purchasing"
        log.log({orderMutation})
      } catch(error: any){
        log.error({loc:"updateCartWithTransactionId", error})
      }
      try {
        const { error } = await this.stripe.confirmPayment({
          elements: this.elements,
          confirmParams: {
            return_url: this.returnUrl,
            receipt_email: authUser?.email,
          },
        });
        log.error({error})
        if (error.type === "card_error" || error.type === "validation_error") {
          this.showMessage(error.message || "Card or validation issue");
        } else {
          this.showMessage("An unexpected error occurred.");
        }
      } catch(error: any){
        log.error({loc:"confirmPayment", error})
        this.showMessage("An unexpected error occurred during payment confirmation.");
      }
    }
  }

  showMessage(messageText: string) {
    const messageContainer = document.querySelector("#payment-message");
    if (!messageContainer) return;
    messageContainer.classList.remove("hidden");
    messageContainer.textContent = messageText;

    setTimeout(function () {
      messageContainer.classList.add("hidden");
      messageContainer.textContent = "";
    }, 24000);
  }

}
